import Joyride, { STATUS, EVENTS, ACTIONS, Status } from 'react-joyride';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { debounce } from 'lodash';

import { ProductOnboardingPropsFromRedux } from 'components/ProductOnboarding/ProductOnboardingContainer';
import { ProductOnboardingTooltip } from 'components/ProductOnboarding/ProductOnboardingTooltip';
import { getSteps } from 'components/ProductOnboarding/getSteps';

const MISSING_STEP_TARGET_RETRY_COUNT = 10;

export const ProductOnboarding = ({
  isRunning,
  stepIndex,
  retryTarget,
  setRetryTarget,
  stopTour,
  setIsRunning,
  nextStep,
  isAccountClosed,
  tenantSettings,
  toggleDrawerMenu,
  isDrawerMenuOpen,
  isManualSwitch
}: ProductOnboardingPropsFromRedux) => {
  const history = useHistory();
  const errorTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const errorRetryCountRef = useRef<number>(0);
  const pendingDrawerOpenRef = useRef(false);
  // TODO: Extract breakpoint to the const and align it with tailwind configuration so that it's configured in one place for the whole app
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const { disableBalanceTransfer, disableCloseAccount } = tenantSettings;
  const steps = getSteps({
    disableBalanceTransfer,
    disableCloseAccount: disableCloseAccount || isAccountClosed,
    isManualSwitch,
    isMobile
  });

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth < 768;
      setIsMobile(newIsMobile);

      if (isRunning && newIsMobile && !isDrawerMenuOpen && !pendingDrawerOpenRef.current) {
        pendingDrawerOpenRef.current = true;
        setIsRunning(false);
        toggleDrawerMenu();
      }

      if (!newIsMobile && isDrawerMenuOpen) {
        toggleDrawerMenu();
      }
    };

    const debouncedHandleResize = debounce(handleResize, 150);
    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      debouncedHandleResize.cancel();
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [isDrawerMenuOpen, setIsMobile, toggleDrawerMenu, isRunning, setIsRunning]);

  useEffect(() => {
    if (pendingDrawerOpenRef.current && isDrawerMenuOpen) {
      pendingDrawerOpenRef.current = false;
      setTimeout(() => {
        setIsRunning(true);
      }, 300);
    }
  }, [isDrawerMenuOpen, setIsRunning]);

  useEffect(() => {
    if (!retryTarget) return;

    errorRetryCountRef.current = 0;

    const poll = () => {
      if (errorRetryCountRef.current === MISSING_STEP_TARGET_RETRY_COUNT) {
        errorRetryCountRef.current = 0;
        setRetryTarget(null);
        stopTour();
        return;
      }

      const targetEl = document.querySelector(retryTarget);

      if (targetEl) {
        setRetryTarget(null);
        setIsRunning(true);
        errorRetryCountRef.current = 0;
      } else {
        errorRetryCountRef.current += 1;
        errorTimeoutRef.current = setTimeout(poll, 500);
      }
    };

    errorTimeoutRef.current = setTimeout(poll, 500);

    return () => {
      clearTimeout(errorTimeoutRef.current);
    };
  }, [retryTarget, setIsRunning, setRetryTarget, stopTour]);

  return (
    <>
      <Joyride
        disableScrollParentFix
        steps={steps}
        stepIndex={stepIndex}
        scrollOffset={72} // Header height
        run={isRunning}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        callback={data => {
          const {
            status,
            action,
            type,
            step: {
              data: { routeToBeforeStep, routeToAfterStep, shouldToggleDrawerMenu },
              target
            }
          } = data;

          const finishedStatuses: Partial<Status>[] = [STATUS.FINISHED, STATUS.SKIPPED];

          if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
            stopTour();

            return;
          }

          if (type === EVENTS.TOUR_START && isMobile && !isDrawerMenuOpen) {
            pendingDrawerOpenRef.current = true;
            setIsRunning(false);
            toggleDrawerMenu();
            return;
          }

          if (type === EVENTS.TARGET_NOT_FOUND) {
            setIsRunning(false);
            setRetryTarget(target as string);
          } else if (type === EVENTS.STEP_BEFORE && routeToBeforeStep) {
            history.push(routeToBeforeStep);
          } else if (type === EVENTS.STEP_AFTER) {
            history.push(routeToAfterStep);

            if (shouldToggleDrawerMenu) {
              toggleDrawerMenu();
            }

            setTimeout(() => {
              nextStep();
            }, 100);
          }
        }}
        tooltipComponent={ProductOnboardingTooltip}
        styles={{
          options: { zIndex: 10000 },
          spotlight: { borderRadius: '8px' }
        }}
        floaterProps={{
          hideArrow: true
        }}
      />
    </>
  );
};
